import { FilterIdentifier, getSavedViewIdentifier } from 'models/saved_view'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useApiService from 'services/api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { PiCaretDown, PiCaretUp, PiFloppyDisk, PiX } from 'react-icons/pi'
import { FilterStore } from '../FilterStore'
import { FilterSearchInput } from '../components/FilterSearchInput'

type SelectSavedFilterProps = {
  identifier: FilterIdentifier
  filterStore: FilterStore
  /** Optional controlled selection. When the parent owns this state (e.g.
   *  FilterModal so its "Clear all" can also reset the pill), pass `value`
   *  + `onChange`. Otherwise the component manages it internally. */
  value?: string | number | null
  onChange?: (next: string | number | null) => void
}

/**
 * DS-styled saved-filter selector — mirrors the playground's `DsDropdown`:
 * pill trigger that shows the current selection (with an inline clear X when
 * active), a list of saved views, and a sticky "Manage saved filters…" footer
 * separated by a divider that routes to the settings page.
 */
export default function SelectSavedFilter({
  identifier,
  filterStore,
  value: valueProp,
  onChange,
}: SelectSavedFilterProps) {
  const api = useApiService()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  // Controlled when `valueProp` is provided, uncontrolled otherwise — that
  // way every existing caller keeps working without threading the new props.
  const [internalValue, setInternalValue] = useState<string | number | null>(
    null
  )
  const isControlled = valueProp !== undefined
  const selectedValue = isControlled ? (valueProp ?? null) : internalValue
  const setSelectedValue = (next: string | number | null) => {
    if (!isControlled) setInternalValue(next)
    onChange?.(next)
  }

  const { clearAllFilters, loadSavedFilter } = filterStore()
  // Saved filters scope to the base table type (campaign-of-same-type
  // sharing per FIR-7494), so strip any per-campaign suffix before querying.
  const savedViewIdentifier = getSavedViewIdentifier(identifier)
  const { data: savedViewsOptions } =
    api.useGetSavedFiltersOptions(savedViewIdentifier)

  const selectedLabel = useMemo(() => {
    if (selectedValue == null) return null
    return (
      savedViewsOptions?.find((o) => o.value === selectedValue)?.label ?? null
    )
  }, [selectedValue, savedViewsOptions])

  const isActive = selectedValue != null

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return savedViewsOptions ?? []
    return (savedViewsOptions ?? []).filter((o) =>
      o.label.toLowerCase().includes(q)
    )
  }, [savedViewsOptions, search])

  // Surface the search box whenever there's more than one saved filter to sift
  // through — restores the search the react-select used to provide, while
  // skipping a pointless box when there's only a single saved filter.
  const showSearch = (savedViewsOptions?.length ?? 0) > 1

  // Clear the search on close so a stale query doesn't persist into the next
  // session (mirrors InlineSelectFilter's open handler).
  const handleOpenChange = (next: boolean) => {
    if (!next) setSearch('')
    setOpen(next)
  }

  const apply = (option: {
    value: string | number
    original?: { payload?: any }
  }) => {
    clearAllFilters()
    setSelectedValue(option.value)
    if (option.original?.payload) {
      loadSavedFilter(option.original.payload)
    }
    setOpen(false)
  }

  const clear = (e: React.MouseEvent) => {
    // Both events need stop-propagation: pointerdown is what Radix listens
    // on to open the menu, click is what the parent button uses. Either
    // would otherwise pop the dropdown open immediately after clearing.
    e.preventDefault()
    e.stopPropagation()
    setSelectedValue(null)
    clearAllFilters()
  }

  // Showing the X is an inline span inside the trigger; pointerdown/click
  // here is stopped so it never reaches Radix's trigger.
  const stopPointer = (e: React.PointerEvent | React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    // Below sm (640px) we compact to a square icon button — label + caret +
    // inline X all hide so the header doesn't overflow on phones. Above sm
    // the full pill returns.
    <div className="relative w-9 sm:w-[240px] flex-shrink-0">
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Saved filters"
            title={selectedLabel ?? 'Saved filters'}
            className={cn(
              'flex w-full items-center h-9 rounded-full',
              'justify-center sm:justify-between gap-0 sm:gap-2',
              'pl-0 pr-0 sm:pl-3 sm:pr-3',
              'text-sm font-medium transition-colors',
              'focus:outline-none focus-visible:outline-none',
              '!border',
              // When the X is showing (active + closed) on desktop, reserve
              // 36px on the right so the truncated label has clearance.
              // No-op on mobile since the X is hidden.
              isActive && !open ? 'sm:!pr-9' : '',
              // Distinct from the green filter pills — saved-filters is a
              // utility/meta control (loads configurations rather than
              // editing filter state). The pill shape + neutral-50 fill
              // visually separates these top-level controls from the
              // field-level dropdowns inside the modal body.
              open
                ? '!bg-white !border-neutral-400 !text-neutral-800'
                : isActive
                  ? '!bg-neutral-50 !border-neutral-300 hover:!bg-white hover:!border-neutral-400 !text-brand-active'
                  : '!bg-neutral-50 !border-neutral-300 hover:!bg-white hover:!border-neutral-400 !text-neutral-700'
            )}
          >
            {/* Save icon always visible — mirrors the PiFloppyDisk used on
                the table-row save button so users recognize this as the
                saved-filters affordance. Picks up the brand-active tint
                when a filter is loaded so it reads "saved view active". */}
            <PiFloppyDisk
              size={16}
              className={cn(
                'flex-shrink-0',
                isActive ? 'text-brand-active' : 'text-neutral-500'
              )}
            />
            <span
              // `ml-1` adds 4px on top of the gap-2 so the icon doesn't
              // press up against the label — keeps the trigger breathing.
              className="hidden sm:block truncate text-left flex-1 min-w-0 ml-1"
              title={selectedLabel ?? undefined}
            >
              {selectedLabel ?? 'Saved filters'}
            </span>
            {/* Caret only when not actively clearing; X sits in the absolute
                slot below so it can fully intercept the click. Hidden on
                mobile where the trigger is icon-only. */}
            {open ? (
              <PiCaretUp
                size={14}
                className="hidden sm:block text-neutral-500"
              />
            ) : !isActive ? (
              <PiCaretDown
                size={14}
                className="hidden sm:block text-neutral-500"
              />
            ) : null}
          </button>
        </DropdownMenuTrigger>
        {isActive && !open && (
          <button
            type="button"
            aria-label="Clear saved filter"
            onPointerDown={stopPointer}
            onMouseDown={stopPointer}
            onClick={clear}
            // Hidden on mobile where the trigger is icon-only — there's no
            // room and the user can clear via the dropdown items instead.
            className="hidden sm:inline-flex absolute top-0 bottom-0 right-2 my-auto items-center justify-center h-5 w-5 rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <PiX size={14} />
          </button>
        )}
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="!rounded-xl !border-neutral-200 !shadow-lg !bg-white w-[260px] !p-0 !overflow-hidden"
        >
          {showSearch && (
            <div className="border-b border-neutral-200 p-1.5">
              <FilterSearchInput
                value={search}
                onChange={setSearch}
                isFocused={searchFocused}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search saved filters"
              />
            </div>
          )}
          <div className="max-h-[280px] overflow-y-auto p-1.5">
            {savedViewsOptions && savedViewsOptions.length > 0 ? (
              filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const active = option.value === selectedValue
                  return (
                    <button
                      type="button"
                      key={String(option.value)}
                      onClick={() => apply(option)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                        'hover:bg-neutral-100',
                        active
                          ? 'bg-neutral-100 text-neutral-900 font-semibold'
                          : 'text-neutral-700'
                      )}
                    >
                      <span className="truncate block">{option.label}</span>
                    </button>
                  )
                })
              ) : (
                <p className="px-3 py-2 text-sm text-neutral-500 m-0">
                  No matching saved filters
                </p>
              )
            ) : (
              <p className="px-3 py-2 text-sm text-neutral-500 m-0">
                No saved filters yet
              </p>
            )}
          </div>
          <div className="border-t border-neutral-200 p-1.5 bg-white">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/settings/data/filters')
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              Manage saved filters…
            </button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
